import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { DoorState, LockState } from "../types";
import type { FridgeDoorLockState } from "../types/mainTypes";
import { selectActiveLocalTransaction } from "./transactionSlice";

type DoorSlice = {
	door: {
		state: DoorState | null;
		lock: {
			state: LockState | null;
			timeout: number | null;
		};
	};
	// The transaction during which the door opened. Written by doorOpenedMiddleware, whichever path
	// changed the door or the slot; a transaction that takes the slot from another one does not match.
	doorOpenedDuringTransactionId: string | null;
};

const initialState: DoorSlice = {
	door: {
		state: DoorState.Closed,
		lock: {
			state: LockState.Locked,
			timeout: 20,
		},
	},
	doorOpenedDuringTransactionId: null,
};

export const doorSlice = createSlice({
	name: "door",
	initialState: initialState,
	reducers: {
		setDoorState: (state, { payload }: PayloadAction<DoorState>) => {
			state.door.state = payload;
		},
		setLockState: (state, { payload }: PayloadAction<LockState>) => {
			state.door.lock.state = payload;
		},
		setLockTimeout: (state, { payload }: PayloadAction<number>) => {
			state.door.lock.timeout = payload;
		},
		setDoorLockState: (state, { payload }: PayloadAction<FridgeDoorLockState>) => {
			state.door = payload;
		},
		setDoorOpenedDuringTransactionId: (state, { payload }: PayloadAction<string | null>) => {
			state.doorOpenedDuringTransactionId = payload;
		},
	},
});

export const { setDoorState, setLockState, setLockTimeout, setDoorLockState, setDoorOpenedDuringTransactionId } =
	doorSlice.actions;

export const selectDoorState = (state: RootState) => state.door?.door?.state;
export const selectLockState = (state: RootState) => state.door?.door?.lock.state;
export const selectLockTimeout = (state: RootState) => state.door?.door?.lock.timeout;
export const selectDoorOpenedDuringTransactionId = (state: RootState) => state.door.doorOpenedDuringTransactionId;

/** Whether the door opened during the transaction that holds the slot: an order or a local refill. */
export const selectHasDoorBeenOpened = createSelector(
	[selectDoorOpenedDuringTransactionId, selectActiveLocalTransaction],
	(openedDuring, transaction) => openedDuring !== null && openedDuring === transaction?.id,
);

export default doorSlice.reducer;
